import {
    buildNativeClass,
    lookupSpecial,
    promiseToSuspension,
    pyCallable,
    pyException,
    pyFunc,
    pyNewableType,
    pyNone,
    pyObject,
    pyStr,
    pyTuple,
    suspensionToPromise,
} from "@Sk";
import PyDefUtils from "PyDefUtils";
import * as componentModule from "@runtime/components";
import { Component } from "@runtime/components/Component";
import { Container } from "@runtime/components/Container";
import { setupDefaultAnvilPluggableUI } from "@runtime/modules/_anvil/pluggable-ui";
import * as serverModule from "@runtime/modules/server";
import { pyDesignerApi } from "@runtime/runner/component-designer-api";
import { pyPropertyUtilsApi } from "@runtime/runner/component-property-utils-api";
import { jsCustomComponents, registerJsPythonModules } from "@runtime/runner/components-in-js/common";
import { AppConfig, FormYaml, temporaryHackSetupData } from "@runtime/runner/data";
import { createFormTemplateClass } from "@runtime/runner/forms";
import { Slot, WithLayout } from "@runtime/runner/python-objects";
import { readBuiltinFiles } from "@runtime/runner/read-builtin-files";
import designerComponentsModule from "../components";
import {
    AppIndex,
    TemplateSpec,
    appConfig,
    appIndex,
    findDesignerTemplates,
    findSourceCode,
    getCodeItem,
    getMultiFormYaml,
    getSourceCode,
    isForm,
    mainPackageName,
    setApp,
} from "./app-content";
import { stdout } from "./python-output-and-errors";

const readSource = (path: string) => {
    // Does this file correspond to an app resource?
    const sourceSpec = findSourceCode(path);
    if (sourceSpec) {
        return promiseToSuspension(getSourceCode(sourceSpec));
    }

    // Does this file correspond to a module full of designer templates?
    const templateSpec = findDesignerTemplates(path);
    if (templateSpec) {
        return promiseToSuspension(buildTemplateModule(templateSpec));
    }

    // Otherwise serve from Skulpt builtins
    return readBuiltinFiles(path);
};

window.anvilCurrentlyConstructingForms = [];
window.anvilFormTemplates = [];

const buildTemplateModule = async (templateSpec: TemplateSpec) => {
    const forms: FormYaml[] = await getMultiFormYaml(templateSpec);
    let body = "";
    for (const yaml of forms) {
        const leafName = yaml.class_name.replace(/^.*\./, "");
        const idx = window.anvilFormTemplates.length;
        body += `anvilFormTemplates[${idx}], (template) => { mod[${JSON.stringify(
            leafName + "Template"
        )}] = template; },`;
        window.anvilFormTemplates.push(() =>
            createFormTemplateClass(yaml, templateSpec.depId, leafName, BASE_MODULES["anvil"])
        );
    }
    return `function $builtinmodule(mod) { return Sk.misceval.chain(null, ${body} () => mod); }`;
};

export const resetLoadedModules = () => {
    Sk.sysmodules.tp$getattr<pyCallable>(new pyStr("clear")).tp$call([]);
    Sk.configure({
        output: stdout,
        read: readSource,
        syspath: ["app", "anvil-services"],
        __future__: appConfig.runtime_options?.client_version == "3" ? Sk.python3 : Sk.python2,
        suspensionHandlers: PyDefUtils.suspensionHandlers,
    });
    Sk.importSetUpPath(); // This is hack - normally happens automatically on first import

    // Reload the Anvil standard modules
    for (const [name, modvars] of Object.entries(BASE_MODULES)) {
        PyDefUtils.loadModule(name, modvars);
    }

    setupDefaultAnvilPluggableUI(BASE_MODULES["anvil"]);

    Sk.sysmodules.mp$ass_subscript(new pyStr("anvil.js.window"), BASE_MODULES["anvil.js"].window);
    // Special-case anvil.server, which needs anvil loaded first (ugh)
    PyDefUtils.loadModule("anvil.server", (serverModule as any).default(null, debugOrigin).pyMod);
};

const mkStub = (pkg: string, name: string, extraAttributes = {}) => ({
    __name__: new pyStr(name),
    __package__: new pyStr(pkg),
    __path__: new pyTuple([new pyStr("builtin-does-not-exist/")]),
    __getattr__: new pyFunc(function (pyName) {
        throw new pyException(`Cannot use the "${name}" module in the designer ${pyName}`);
    }),
    ...extraAttributes,
});

interface StubObject extends pyObject {
    _name: string;
    _msg: string;
}

const StubObject: pyNewableType<StubObject> = buildNativeClass("DesignerStub", {
    constructor: function StubObject(name) {
        this._name = name;
        this._msg = `Cannot use ${name} in the designer`;
    },
    slots: {
        tp$getattr() {
            throw new pyException(this._msg);
        },
        tp$setattr() {
            throw new pyException(this._msg);
        },
        tp$call() {
            throw new pyException(this._msg);
        },
    },
});

const getBaseModules = () => {
    Sk.configure({ __future__: Sk.python3 });

    // @ts-ignore
    const anvilModule = require("@runtime/modules/anvil")(null, {});
    anvilModule["Component"] = Component;
    anvilModule["Container"] = Container;
    anvilModule["Slot"] = Slot;
    anvilModule["WithLayout"] = WithLayout;

    componentModule.defineSystemComponents(anvilModule);
    designerComponentsModule.defineDesignerComponents(anvilModule);

    const EXPLODE_FUNCS = (module: string, ...names: string[]) =>
        Object.fromEntries(
            names.map((n) => [
                n,
                () => {
                    throw new pyException(`Cannot call ${module}.${n} in the designer`);
                },
            ])
        );
    const NONE_FUNCS = (...names: string[]) => Object.fromEntries(names.map((n) => [n, () => pyNone]));

    /**
     * NOTE: these modules get put into sys modules
     * BE CAREFUL when adding modules here
     * a module in the `anvil` namespace is ok
     * for non anvil modules see loadServices below
     */
    return {
        anvil: anvilModule,
        "anvil.designer": pyDesignerApi,
        "anvil.property_utils": pyPropertyUtilsApi,
        // @ts-ignore
        "anvil.js": require("@runtime/modules/js")(),
        "anvil.tables": mkStub("anvil.tables", "tables", {
            app_tables: new StubObject("anvil.tables.app_tables"),
            TableError: new StubObject("anvil.tables.TableError"),
            order_by: new StubObject("anvil.tables.order_by"),
        }),
        "anvil.tables.query": mkStub("anvil.tables", "query"),
        "anvil.server": mkStub("anvil", "server"),
        "anvil.users": mkStub("anvil", "users", {
            ...NONE_FUNCS("get_user"),
            ...EXPLODE_FUNCS("anvil.users", "login"),
        }),
        "anvil.email": mkStub("anvil.email", "email"),
        "anvil.google": mkStub("anvil.google", "google"),
        "anvil.google.auth": mkStub(
            "anvil.google",
            "auth",
            NONE_FUNCS("get_user_email", "get_user_access_token", "get_user_refresh_token")
        ),
        "anvil.google.drive": mkStub("anvil.google", "drive", {
            app_files: new StubObject("anvil.google.drive.app_files"),
            ...EXPLODE_FUNCS("anvil.google.drive", "login"),
        }),
        "anvil.google.mail": mkStub("anvil.google", "mail"),
        "anvil.google.sheets": mkStub("anvil.google", "sheets"),
        "anvil.facebook": mkStub("anvil.facebook", "facebook"),
        "anvil.facebook.auth": mkStub("anvil.facebook", "auth", EXPLODE_FUNCS("anvil.facebook.auth", "login")),
        "anvil.saml": mkStub("anvil.saml", "saml"),
        "anvil.saml.auth": mkStub("anvil.saml", "auth", EXPLODE_FUNCS("anvil.saml.auth", "login")),
        "anvil.stripe": mkStub("anvil", "stripe"),
        "anvil.media": mkStub("anvil", "media"),
        "anvil.microsoft": mkStub("anvil.microsoft", "microsoft"),
        "anvil.microsoft.auth": mkStub("anvil.microsoft", "auth", {
            ...EXPLODE_FUNCS("anvil.microsoft.auth", "login"),
            ...NONE_FUNCS("get_user_email", "get_user_id", "get_user_access_token", "get_user_refresh_token"),
        }),
        // @ts-ignore
        "anvil.tz": require("@runtime/modules/tz")(),
        "anvil.http": mkStub("anvil", "http", {
            UrlEncodingError: new StubObject("anvil.http.UrlEncodingError"),
            HttpError: new StubObject("anvil.http.HttpError"),
            ...EXPLODE_FUNCS(
                "anvil.http",
                "url_encode",
                "url_decode",
                "json_stringify",
                "b64_encode",
                "b64_decode",
                "request"
            ),
        }),
        // we allow the history module to be used as is - this shouldn't effect anything
        // but if it does, we will need to ensure the Location object is available in the Designer
        "anvil.history": require("@runtime/modules/history").default,
        // @ts-ignore
        "anvil.image": require("@runtime/modules/image")(),
        "anvil.code_completion_hints": require("@runtime/modules/code-completion-hints")(),
        base64: require("@runtime/modules/base64")(),
        "anvil.xml": require("@runtime/modules/base64")(),
        "anvil.regex": require("@runtime/modules/regex")(),
        "anvil.tableau": mkStub("anvil", "tableau"),
    };
};

const pyStubModule = `
def __getattr__(name):
  raise RuntimeError(f"{__name__}.{name} is not supported in the designer")
`;

/** better to put anything that's not in the anvil namespace here */
function loadServices() {
    Object.assign(Sk.builtinFiles.files, {
        ["anvil-services/stripe/__init__.py"]: "",
        ["anvil-services/stripe/checkout.py"]: pyStubModule,
        ["anvil-services/tables/__init__.py"]: "app_tables = object()" + pyStubModule,
        ["anvil-services/google/__init__.py"]: "",
        ["anvil-services/google/auth.py"]: pyStubModule,
        ["anvil-services/google/drive.py"]: "app_files = object()" + pyStubModule,
        ["anvil-services/google/sheets.py"]: pyStubModule,
        ["anvil-services/google/mail.py"]: pyStubModule,
        ["anvil-services/raven/__init__.py"]: "",
        ["anvil-services/raven/auth.py"]: pyStubModule,
    });
}

loadServices();

export const BASE_MODULES = getBaseModules();

let debugOrigin: string;

export const setDebugOrigin = (origin: string) => {
    debugOrigin = origin;
};

export const setUpEnvironmentForApp = async (appConfig: AppConfig, appIndex: AppIndex) => {
    setApp(appConfig, appIndex);
    resetLoadedModules();

    // We'll want a way of updating these at some point (...and keeping them somewhere less gross, natch)
    // @ts-ignore
    BASE_MODULES.anvil["HtmlTemplate"].$_anvilThemeAssets = new Proxy(window.anvilThemeHtml || {}, {
        get(target, prop: string): any {
            // One day we'll lazy-load the asset like this, but for now this getter isn't allowed to suspend, so just use this to signal to the IDE
            // that we depend on this asset.
            // Assets are referred to directly by name, with no dep ID, so might not exist in this app. Be sure to cope with this at the other end.
            getCodeItem("asset", prop, null);
            return target[prop];
        },
    });

    // TODO this is a very temporary nasty hack until we set up designer instantiation hooks that don't use any of these.
    // (we might need to set up data with *something* later, but it shouldn't be this:
    temporaryHackSetupData({
        app: {
            // @ts-expect-error
            dependency_code: Object.fromEntries(
                Object.entries(appIndex.dependencies).map(([depId, dep]) => [
                    depId,
                    {
                        config: dep.config,
                        package_name: dep.packageName,
                        client_init_module: dep.clientInitModule,
                    },
                ])
            ),
            config: appIndex.config,
            runtime_options: appConfig.runtime_options,
        },
        appPackage: mainPackageName,
        dependencyPackages: Object.fromEntries(
            Object.entries(appIndex.dependencies).map(([depId, { packageName }]) => [depId, packageName!])
        ),
        logicalDepIds: appConfig.dependency_ids,
    });

    // Also set the top-level packages up
    PyDefUtils.loadModule(mainPackageName, {
        __name__: new pyStr(mainPackageName),
        __package__: new pyStr(mainPackageName),
        __path__: new pyTuple([new pyStr(`app/${mainPackageName}`)]),
    });
    for (const [_, { packageName }] of Object.entries(appIndex.dependencies)) {
        if (!packageName) continue;
        PyDefUtils.loadModule(packageName, {
            __name__: new pyStr(packageName),
            __package__: new pyStr(packageName),
            __path__: new pyTuple([new pyStr(`app/${packageName}`)]),
        });
    }

    for (const [moduleName, { pyMod }] of Object.entries(jsCustomComponents)) {
        Sk.sysmodules.mp$ass_subscript(new pyStr(moduleName), pyMod);
    }

    registerJsPythonModules();

    for (const depId of appIndex.dependencyOrder) {
        const { packageName, clientInitModule } = appIndex.dependencies[depId] ?? {};
        if (packageName && clientInitModule) {
            await Sk.misceval.asyncToPromise(() => Sk.importModule(`${packageName}.${clientInitModule}`, false, true));
        }
    }

    if (appIndex.clientInitModule) {
        await Sk.misceval.asyncToPromise(() =>
            Sk.importModule(`${mainPackageName}.${appIndex.clientInitModule}`, false, true)
        );
    }
};

export async function resetEnvironmentForApp() {
    await setUpEnvironmentForApp(appConfig, appIndex);
}

// TODO should this be in a utils module somewhere?
export function getLocalFormName(pyComponent: Component) {
    if (pyComponent.anvil$yamlCarrier) {
        const { depId, formName } = pyComponent.anvil$yamlCarrier;
        if (!depId) {
            return formName;
        }
    } else {
        const moduleName = lookupSpecial(pyComponent.ob$type, pyStr.$module)?.toString();
        if (moduleName?.startsWith(mainPackageName + ".")) {
            const formName = moduleName.substring(mainPackageName.length + 1);
            if (isForm(null, formName)) {
                return formName;
            }
        }
    }
}
